import ICart from '../models/ICart';

/**
 * Line total of a cart item, in reais.
 *
 * This is the expression `cartTotal` has always used, kept verbatim so both the charged total and
 * the analytics revenue come from the same place. It carries two non-obvious rules: an item that
 * belongs to a pizza size ignores `unitPrice`, and a custom pizza ignores its complements because
 * the configured price is already consolidated into `unitPrice`.
 *
 * Not to be confused with `getCartItemTotal` in `utils/cart.ts`, which applies the GREATER pizza
 * charge and serves a different caller.
 */
export const getCartLineTotal = (item: ICart): number => {
  if (item.isCustomPizza) return item.unitPrice * item.qty;

  return (
    (!item.pizzaCategoryId ? item.unitPrice : 0) * item.qty +
    item.complementsGroups.reduce((groupSum, group) => {
      return (
        groupSum +
        group.complements.reduce((complementSum, complement) => {
          return complementSum + complement.amount * complement.unitPrice * item.qty;
        }, 0)
      );
    }, 0)
  );
};

export const getCartTotal = (items: ICart[]): number =>
  items.reduce((sum, item) => sum + getCartLineTotal(item), 0);

export const toCents = (value: number): number => Math.round(value * 100);

export const fromCents = (cents: number): number => cents / 100;

/**
 * Spreads a cart wide discount across the lines, proportionally to what each one is worth.
 *
 * Works in integer cents and settles the rounding leftover by the largest remainder method, so the
 * allocated cents always add up to the discount exactly. Ties break by position, which keeps the
 * same cart producing the same allocation on every call.
 */
export const allocateDiscount = (grossCents: number[], discountCents: number): number[] => {
  const total = grossCents.reduce((sum, gross) => sum + gross, 0);

  if (discountCents <= 0 || total <= 0) return grossCents.map(() => 0);

  const capped = Math.min(discountCents, total);
  const shares = grossCents.map((gross) => (capped * gross) / total);
  const allocated = shares.map((share) => Math.floor(share));

  let remaining = capped - allocated.reduce((sum, cents) => sum + cents, 0);

  const byRemainder = shares
    .map((share, index) => ({ index, remainder: share - Math.floor(share) }))
    .sort((left, right) => right.remainder - left.remainder || left.index - right.index);

  for (let position = 0; remaining > 0 && position < byRemainder.length; position += 1) {
    allocated[byRemainder[position].index] += 1;
    remaining -= 1;
  }

  return allocated;
};

export interface IPricedEntry {
  priceCents: number;
  discountCents: number;
  quantity: number;
}

/**
 * Turns one cart line into the analytics entries that reproduce it exactly, both in price and in
 * discount.
 *
 * A unit price has two decimals, so some line totals have no exact unit price: 998 cents over 3
 * units is one of them. The line is therefore split by the division remainder, giving unit prices
 * that do add up — which is what keeps `value` equal to the sum of `price * quantity`.
 *
 * The gross and the net can land on different remainders, so both are split and the units grouped
 * by the pair they fall into. Each entry then carries `discount = grossUnit - netUnit`, an integer,
 * and the entries add up to the line gross, the line net and the line discount at the same time.
 * Three groups is the worst case; a line with no discount, or whose discount divides evenly, comes
 * back as one or two.
 */
export const splitLineIntoEntries = (
  grossCents: number,
  discountCents: number,
  quantity: number,
): IPricedEntry[] => {
  if (quantity <= 0) return [];

  const netCents = grossCents - discountCents;

  const netBase = Math.floor(netCents / quantity);
  const netRemainder = netCents - netBase * quantity;
  const grossBase = Math.floor(grossCents / quantity);
  const grossRemainder = grossCents - grossBase * quantity;

  // The extra cent goes to the first units of the line, for the net and for the gross alike, so a
  // unit belongs to at most three combinations of the two.
  const both = Math.min(netRemainder, grossRemainder);
  const onlyOne = Math.abs(netRemainder - grossRemainder);
  const neither = quantity - Math.max(netRemainder, grossRemainder);

  const middleNet = netRemainder > grossRemainder ? netBase + 1 : netBase;
  const middleGross = grossRemainder > netRemainder ? grossBase + 1 : grossBase;

  const groups = [
    { priceCents: netBase + 1, grossCents: grossBase + 1, quantity: both },
    { priceCents: middleNet, grossCents: middleGross, quantity: onlyOne },
    { priceCents: netBase, grossCents: grossBase, quantity: neither },
  ];

  return groups
    .filter((entry) => entry.quantity > 0)
    .map(({ priceCents, grossCents: unitGross, quantity: entryQuantity }) => ({
      priceCents,
      discountCents: unitGross - priceCents,
      quantity: entryQuantity,
    }));
};
